/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
**/

/**
 * Adds a `geo-country-xx` class (lowercased ISO code) to <html> from Shopify's native
 * geo-IP lookup — the same endpoint the country popup below uses. Sections opt in via
 * CSS; see the import duties notice in sections/mini-cart.liquid.
 *
 * Kept separate from the popup logic below because that only runs its lookup when the
 * "countryPopup" localStorage key is empty, so it stops resolving a country for 24h once
 * the popup is dismissed. The class goes on <html> rather than inside the cart drawer
 * because <cart-drawer> replaces its own innerHTML on every cart update.
 */
(function () {
    var CACHE_KEY = 'geoCountry';
    var CACHE_TTL = 86400000; // 24h, matching the country popup's window

    function applyCountry(code) {
        if (code) {
            document.documentElement.classList.add('geo-country-' + code.toLowerCase());
        }
    }

    var cached = null;

    try {
        cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    } catch (e) {
        cached = null;
    }

    if (cached && cached.code && (new Date().getTime() - cached.timestamp) < CACHE_TTL) {
        applyCountry(cached.code);
        return;
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'browsing_context_suggestions.json?country[enabled]=true')
        .then(function (r) { return r.json(); })
        .then(function (data) {
            var code = (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '';

            if (!code) {
                return;
            }

            applyCountry(code);

            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({ code: code, timestamp: new Date().getTime() }));
            } catch (e) {}
        })
        .catch(function () {});
})();

$(document).ready(function () {
    // Check if there is a local storage item
    var currentUrl = window.location.hostname;

    if (localStorage.getItem("countryPopup")) {

        var countryInfo = JSON.parse(localStorage.getItem("countryPopup"));
        var dateString = countryInfo.timestamp;
        var now = new Date().getTime().toString();

        // Clear localStorage if timestamp more than 24 hours (86400000 ms)
        if (now - dateString > 86400000) {
            localStorage.setItem('countryPopup', '');
        }
    } else {
        // Shopify's native geo-IP via browsing_context_suggestions.json
        // https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
        var euCountries = ['AD','AL','AT','BA','BE','BG','BY','CH','CY','CZ','DE','DK','EE','ES','FI','FO','FR','GB','GG','GI','GR','HR','HU','IE','IM','IS','IT','JE','LI','LT','LU','LV','MC','MD','ME','MK','MT','NL','NO','PL','PT','RO','RS','RU','SE','SI','SJ','SK','SM','UA','VA','XK'];
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        fetch(root + 'browsing_context_suggestions.json?country[enabled]=true')
            .then(function (r) { return r.json(); })
            .then(function (data) {
                var detected = (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '';
                var isEU = euCountries.indexOf(detected) !== -1;

                if (isEU && currentUrl == 'us.glo910.com') {
                    $('.popup-country').show();
                    // Hide discount popup
                    $('.subscription-popup--container').removeClass('popup--show');
                    if (detected != 'ES') {
                        $('.flag__europe').attr('href', 'https://www.glo910.com/en');
                    }
                } else if (!isEU && currentUrl == 'www.glo910.com') {
                    $('.popup-country').show();
                    // Hide discount popup
                    $('.subscription-popup--container').removeClass('popup--show');
                } else {
                    openPopup();
                }
            })
            .catch(function () { openPopup(); });
    }

    $('.current-store').on('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        $('.popup-country').hide();
        // Show discount popup
        setTimeout(function () {
            $('.subscription-popup--container').addClass('popup--show');
        }, 2000);
        var countryDate = { value: "shown", timestamp: new Date().getTime() }
        localStorage.setItem('countryPopup', JSON.stringify(countryDate));
    });

    // Mum collection
    $('.trigger-play').on('click touch', function (e) {
        e.preventDefault();
        let video = $(this).closest('.product-grid__video-wraper').find('video');
        $(this).hide();
        video.addClass('active-video').get(0).play();
    });

    $(document).on('click touch', '.active-video', function (e) {
        e.preventDefault();
        let video = $(this);
        video.closest('.product-grid__video-wraper').find('.trigger-play').show();
        video.removeClass('active-video').get(0).pause();
        video.get(0).currentTime = 0;
        video.get(0).load();
    })


    // Impact slider

    $('.impact__myanmar-slider').slick({
        centerMode: true,
        centerPadding: '16px',
        arrows: false,
        dots: false,
        autoplay: true,
        autoplaySpeed: 2000,
        slidesToShow: 1,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    slidesToShow: 2,
                    centerPadding: '0px',
                }
            }
        ]
    });

    //  Usage videos

    $("[data-open-lightbox]").on("click touch", function () {
        var lightbox = $(this).attr("href");
        lity(lightbox);
        var video = $(lightbox).find('video');
        video.get(0).currentTime = 0;
        video.get(0).play();
    });

    $(document).on('lity:close', function (event, instance) {
        $('video').each(function () {
            $(this).get(0).pause();
        });
    });
});

// document.addEventListener('cart:updated', function(event) {
//     var cart = event.detail.cart; // Get the updated content of the cart
//     const checkGift = (element) => element.variant_id === 39334850461809;
//     const giftInCart = cart.items.some(checkGift)

//     const variantIdList = [39587598631025, 39537442127985, 39863670308977, 39797890383985, 31903500501105]; // your variant ids

//     const checkDevice = (element) => variantIdList.includes(element.variant_id);
//     const deviceInCart = cart.items.some(checkDevice);


//     if(deviceInCart && !giftInCart) {
//       $.ajax({
//         url: '/cart/add.js',
//         type: 'post',
//         dataType: 'json',
//         data: 'quantity=1&id=39334850461809',
//         success: function (result){
//           setTimeout(function(){
//             document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
//               bubbles: true
//             }));
//           }, 1000);  
//         }
//     });
//     }
//   });


$(document).ready(function () {
    $('.pvr-videos-slider-3').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 3,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

    $('.pvr-videos-slider-4').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 4,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

    $('.pvr-videos-slider-5').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 3,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

    $('.pvr-videos-slider-6').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 3,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

    $('.pvr-videos-slider-2').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 2,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

    // Initially play video in the first slide
    // $('.slick-current video').each(function () {
    //     $(this).get(0).play();

    // });

    // New event listener for pause event on all video elements
    // $('.video-content').on('pause', function () {
    //     var video = $(this).get(0);
    //     // video.currentTime = 0; // Reset the video
    //     // video.load(); // Reload the video to show poster
    //     video.controls = false; // Disable video controls

    //     $(this).siblings('.pause-video').hide();
    //     $(this).siblings('.play-video').show();
    //     $(this).siblings('.toggle-audio').hide().text('Sound'); // Also hide and reset sound button
    // });


    // Event listener for play button
    $('.play-video').on('click', function () {
        // Pause and reset all videos
        $('.video-content').each(function () {
            var video = $(this).get(0);
            video.pause();
            // video.currentTime = 0; // Reset the video
            // video.load(); // Ensure poster is shown
            video.controls = false; // Disable video controls
        });

        // Show all play buttons and update sound buttons
        $('.play-video').not(this).show();
        $('.toggle-audio').text('Sound');

        // Play the clicked video
        var video = $(this).siblings('.video-content').get(0);
        video.play();
        video.muted = false; // Unmute the video
        video.controls = true; // Enable video controls

        // Show and update this sound button
        // $(this).siblings('.toggle-audio').show().text('Mute');

        // Show and update this pause button
        $(this).siblings('.pause-video').show();

        // Hide this play button
        $(this).hide();
    });


    // Event listener for audio toggle button
    $('.toggle-audio').on('click', function () {
        var video = $(this).siblings('.video-content').get(0);
        video.muted = !video.muted;
        $(this).text(video.muted ? 'Sound' : 'Mute'); // Change button text based on state
    });

    // Event listener for pause button
    $('.pause-video').on('click', function () {
        var video = $(this).siblings('.video-content').get(0);
        video.pause();
        // video.currentTime = 0; // Reset the video
        // video.load(); // Ensure poster is shown
        video.controls = false; // Disable video controls

        // Hide pause button and show play button
        $(this).hide();
        $(this).siblings('.play-video').show();
        $(this).siblings('.toggle-audio').hide().text('Sound'); // Also hide and reset sound button
    });


    // Initially play video in the first slide
    if (window.innerWidth > 768) {
        $('.slick-current video').each(function () {
            $(this).get(0).play();
        });
    }
});



$(document).ready(function () {
    $('.testimonials-slider').slick({
        centerMode: true,
        arrows: false,
        centerPadding: '60px',
        slidesToShow: 3,
        dots: true,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '40px',
                    slidesToShow: 1,
                },
            },
        ],
    });

});

document.addEventListener("DOMContentLoaded", function () {
    var slides = document.querySelectorAll('.pvr-results-card-before-after-slide');

    slides.forEach(slide => {
        slide.addEventListener('click', function () {
            // Get the parent `.pvr-results-card` of the clicked slide
            var parentCard = this.closest('.pvr-results-card');

            // Find all container and slide elements within the same `.pvr-results-card`
            var containers = parentCard.querySelectorAll('.img-comp-container');
            var siblingSlides = parentCard.querySelectorAll('.pvr-results-card-before-after-slide');

            // Remove active class from all containers within the same card
            containers.forEach(container => container.classList.remove('active'));

            // Reset all slides to inactive within the same card
            siblingSlides.forEach(s => s.classList.remove('active'));

            // Add active class to the clicked slide
            this.classList.add('active');

            // Get the target attribute from the clicked slide
            var targetId = this.getAttribute('target');
            var targetContainer = parentCard.querySelector(targetId);

            if (targetContainer) {
                targetContainer.classList.add('active');
                // Initialize the comparisons for the container only if not already initialized
                initComparisonsForContainer(targetContainer);
            }
        });
    });

    // Initialize comparison for all initially active containers
    var initiallyActiveContainers = document.querySelectorAll('.img-comp-container.active');
    initiallyActiveContainers.forEach(container => {
        initComparisonsForContainer(container);
    });
});

function initComparisonsForContainer(container) {
    /* Find all 'overlay' elements that have not been initialized in the specified container */
    var overlays = container.querySelectorAll(".img-comp-overlay:not([data-initialized])");
    overlays.forEach(overlay => {
        compareImages(overlay);
        overlay.setAttribute('data-initialized', 'true'); // Mark as initialized
    });
}

function compareImages(img) {
    var slider, clicked = 0, w, h;
    w = img.offsetWidth;
    h = img.offsetHeight;
    img.style.width = (w / 2) + "px";
    slider = document.createElement("DIV");
    slider.setAttribute("class", "img-comp-slider");
    img.parentElement.insertBefore(slider, img);
    slider.style.top = (h / 2) - (slider.offsetHeight / 2) + "px";
    slider.style.left = (w / 2) - (slider.offsetWidth / 2) + "px";

    slider.addEventListener("mousedown", slideReady);
    window.addEventListener("mouseup", slideFinish);
    slider.addEventListener("touchstart", slideReady);
    window.addEventListener("touchend", slideFinish);

    function slideReady(e) {
        e.preventDefault();
        clicked = 1;
        window.addEventListener("mousemove", slideMove);
        window.addEventListener("touchmove", slideMove);
    }

    function slideFinish() {
        clicked = 0;
    }

    function slideMove(e) {
        if (clicked === 0) return;
        var pos = getCursorPos(e);
        pos = Math.max(0, Math.min(pos, w));
        slide(pos);
    }

    function getCursorPos(e) {
        e = (e.changedTouches) ? e.changedTouches[0] : e;
        var a = img.getBoundingClientRect();
        var x = e.pageX - a.left - window.pageXOffset;
        return x;
    }

    function slide(x) {
        img.style.width = x + "px";
        slider.style.left = img.offsetWidth - (slider.offsetWidth / 2) + "px";
    }
}

// Bolsa verano

document.body.addEventListener('click', function (event) {
    // Check if the clicked element has the class 'donation-swatch'
    if (event.target.classList.contains('donation-swatch')) {
        // Remove 'active' class from all swatches
        document.querySelectorAll('.donation-swatch').forEach(swatch => {
            swatch.classList.remove('active');
        });

        // Add 'active' class to the clicked swatch
        event.target.classList.add('active');

        // Retrieve data attributes from the clicked swatch
        const idDonation = event.target.getAttribute('data-id-donation');
        const src = event.target.getAttribute('data-src');

        // Update the input and image src with the new data
        document.getElementById('id-donation').value = idDonation;
        document.getElementById('donation-image').src = src;
    }
});



